import { myConsoleLog } from './core/environment.js';
import { myInput } from './input.js';
import { cameras } from './view/cameras.js';
import { userimage } from './tools/userimage.js';
import { myToolbar } from './tools/toolbar.js';
import { doBeading } from './tools/beading.js';
import { drag } from './tools/drag.js';
import { marquee } from './tools/marquee.js';
import { rectangle } from './tools/rectangle.js';
import { beadLibrary } from './tools/beadLibrary.js';
import { mirror } from './tools/mirror.js';
import { beads } from './beads.js';
import { settings } from './settings.js';
import { menu } from './menu.js';
import { palette } from './palette.js';
import { paint } from './tools/paint.js';
import { ruler } from './tools/ruler.js';

export const modes = {};

modes.arr = [
    {
        name: 'hand',
        tool: 'Hand',
        noUnselectAll: true,
        onStart: () => b3.store.mySet('showingPanel', 'view'),
        onClickSwatch: () => modes.start('draw'),
        onUpdate: () => cameras.pan()
    },
    {
        name: 'draw',
        tool: 'Draw',
        onClickBead: bead => beads.draw(bead),
        overBead: bead => beads.draw(bead),
    },
    {
        name: 'erase',
        tool: 'Erase',
        onClickBead: bead => beads.useEraser(bead),
        overBead: bead => beads.useEraser(bead),
        onClickSwatch: () => modes.start('draw')
    },
    {
        name: 'paint',
        tool: 'Paint',
        onClickBead: bead => {
            const doPaint = () => {
                document.body.style.cursor = 'wait';
                setTimeout(() => {
                    paint(bead);
                }, 20);
            }
            if (b3.store.state.beadSoupActive) {
                doPaint();
            } else if (bead.style !== palette.activeStyleId) {
                doPaint();
            } else if (bead.style === null) {
                doPaint();
            }
        },
    },
    {
        name: 'select',
        tool: 'Select',
        noUnselectAll: true,
        noExitCloneMode: true,
        onStart: () => {
            if (settings.pattern.noDrag) return;
            b3.store.mySet('showingPanel', 'transform');
        },
        onClickBead: bead => beads.useSelectTool(bead),
        overBead: bead => beads.toggleSelect(bead),
        onClickSwatch: () => beads.changeColor(),
        backspace: () => beads.eraseSelected(),
    },
    {
        name: 'wand',
        tool: 'Color Select',
        noUnselectAll: true,
        noExitCloneMode: true,
        onStart: () => {
            if (settings.pattern.noDrag) return;
            b3.store.mySet('showingPanel', 'transform');
        },
        onClickBead: bead => beads.useWandTool(bead),
        onClickSwatch: () => beads.changeColor(),
        backspace: () => beads.eraseSelected(),
    },
    {
        name: 'marquee',
        tool: 'Marquee',
        noUnselectAll: true,
        noExitCloneMode: true,
        onStart: () => {
            if (settings.pattern.noDrag) return;
            b3.store.mySet('showingPanel', 'transform');
        },
        onClickBead: bead => beads.useMarqueeTool(bead),
        onClickCanvas: () => marquee.launch(),
        onClickSwatch: () => beads.changeColor(),
        backspace: () => beads.eraseSelected()
    },
    {
        name: 'start beading',
        noClosePanels: true,
        tool: 'Bead Mode',
        onClickBead: bead => {
            if (settings.pattern.name === 'star' && ['F', 'B'].some(element => element === star.currentView)) {
                return; // no beading mode in star view
            }
            mirror.remove();
            doBeading.start(bead);
        },
        onClickSwatch: () => modes.start('draw')
    },
    {
        name: 'beading',
        noClosePanels: true,
        shouldRefresh: true,
        onClickSwatch: () => modes.start('draw'),
        onUpdate: () => cameras.pan(),
        backspace: () => {
            if (myInput.keyReleased) {
                doBeading.next(true);
                myInput.keyReleased = false;
            }
        }
    },
    {
        name: 'draw marquee',
        noUnselectAll: true,
        noExitCloneMode: true,
        noClosePanels: true,
        onUpdate: () => marquee.draw()
    },
    {
        name: 'rectangle',
        tool: 'Rectangle',
        onClickBead: bead => {
            if (myInput.ALT.isDown) {
                const myMode = modes.arr.find(element => element.name === 'eyedropper');
                myMode.onClickBead(bead);
                return;
            }
            rectangle.start();
        },
        onClickCanvas: () => {
            if (myInput.ALT.isDown) return;
            rectangle.start();
        },
    },
    {
        name: 'draw rectangle',
        noClosePanels: true,
        onUpdate: () => rectangle.draw()
    },
    {
        name: 'image',
        tool: 'Image',
        onStart: () => {
            if (!userimage.image) return;
            b3.store.mySet('showingPanel', 'userImage');
            game.input.setDraggable(userimage.image);
        },
        onClickSwatch: () => modes.start('draw'),
        disableCanvas: true
    },
    {
        name: 'rotate image',
        noClosePanels: true,
        onUpdate: () => {
            if (!myInput.pointerEnabled) return;
            userimage.rotateImage();
        },
        disableCanvas: true
    },
    {
        name: 'rotate',
        tool: 'Rotator',
        noUnselectAll: true,
        onStart: () => {
            b3.store.mySet('showingPanel', 'rotate');
            b3.ee.emit('initializeRotator');
        },
    },
    {
        name: 'mirror',
        tool: 'Mirror',
        onStart: () => {
            if (settings.pattern.specialMirror) {
                if (beads.mirror.active) {
                    mirror.rotate();
                } else {
                    mirror.start();
                }
            } else {
                if (beads.mirror.active) mirror.rotate();
            }
        },
        onClickBead: bead => mirror.start(bead),
        onClickSwatch: () => modes.start('draw'),
    },
    {
        name: 'drag',
        noClosePanels: true,
        noUnselectAll: true,
        noExitCloneMode: true,
        onStart: () => modes.storeMode(),
        onUpdate: () => drag.doDrag(),
        overBead: () => {} // this needs to be here!
    },
    {
        name: 'input',
        tool: 'Bead Library',
        enableTyping: true,
        onStart: () => {
            beadLibrary.openPanel()
        },
        onClickBead: () => {},
        onClickCanvas: () => {},
        onClickSwatch: swatch => b3.ee.emit('beadLibraryQuery', {color: swatch.fillColor, trigger: 'swatch'}),
    },
    {
        name: 'eyedropper',
        tool: 'Eyedropper',
        onClickBead: bead => {
            if (!bead.active) return;
            const myStyle = b3.store.state.swatches.find(element => element.id === bead.style);
            palette.select(myStyle);
        },
        onClickSwatch: () => modes.start('draw')
    },
    {
        name: 'rowshift',
        tool: 'Row Shift',
        onClickBead: bead => beads.rowShift(bead),
        onClickSwatch: () => modes.start('draw')
    },
    {
        tool: 'Ruler',
        name: 'ruler',
        showRuler: true,
        onClickBead: () => modes.start('update ruler'),
        onClickCanvas: () => modes.start('update ruler'),
    },
    {
        tool: 'Ruler',
        name: 'update ruler',
        showRuler: true,
        onUpdate: () => ruler.update()
    },
    {
        tool: 'Palette',
        name: 'editSwatch',
        enableTyping: true,
        showPickers: true,
        onStart: () => {
            const mySwatch = b3.store.state.swatches.find(s => s.selected);
            if (!mySwatch) return;

            b3.ee.emit('editSwatch', mySwatch);
            b3.store.mySet('showingModal', 'swatch-edit');
        },
        onClickBead: () => {},
        onClickCanvas: () => {},
        onClickSwatch: swatch => {
            palette.select(swatch);
            b3.ee.emit('editSwatch', swatch);
        }
    },
    {
        name: 'browser',
        enableTyping: true,
        onStart: () => {
            myInput.pointerEnabled = false;
        }
    },
];

modes.currentMode = modes.arr.find(element => element.name === 'draw');
modes.cloneActive = false;

modes.exitCloneMode = () => {
    modes.cloneActive = false;
    const tool = b3.store.state.tools.find(element => element.texture === 'clone');
    tool.isFocused = false;
};

modes.start = modename => {
    myConsoleLog('starting', modename);

    const myMode = modes.arr.find(element => element.name === modename);

    if (userimage.image) game.input.setDraggable(userimage.image, false);

    myMode.enableTyping ?
        myInput.enableTyping():
        myInput.disableTyping();

    if (!myMode.showRuler) ruler.hide();
    if (!myMode.noClosePanels) menu.closePanels();
    if (!myMode.noUnselectAll) beads.unselectAll();
    if (!myMode.noExitCloneMode) modes.exitCloneMode();

    b3.store.mySet('showingModal', null);

    if (myMode.onStart) myMode.onStart();

    myMode.disableCanvas ?
        beads.canvas.disableInteractive():
        beads.canvas.setInteractive();

    if (myMode.tool) myToolbar.focus(myMode.tool);

    const shouldRefresh = modes.currentMode.shouldRefresh;

    b3.store.mySet('currentMode', myMode);
    modes.currentMode = myMode;

    if (shouldRefresh) beads.refreshCanvas();
};

modes.clickBead = bead => {
    if (!myInput.pointerEnabled) return;
    if (!modes.currentMode.onClickBead) return;
    modes.currentMode.onClickBead(bead);
};

modes.clickCanvas = () => {
    if (!myInput.pointerEnabled) return;
    if (!modes.currentMode.onClickCanvas) return;
    modes.currentMode.onClickCanvas();
};

modes.overBead = bead => {
    if (!myInput.pointerEnabled) return;
    if (!modes.currentMode.overBead) return;
    modes.currentMode.overBead(bead);
};

modes.storeMode = () => {
    modes.recentMode = modes.currentMode;
};

modes.restoreMode = () => {
    modes.start(modes.recentMode.name);
};

export const update = () => {
    if (!b3.store.state.hasBooted) return;
    if (!modes.currentMode.onUpdate) return
    modes.currentMode.onUpdate();
};